import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, CheckCircle2, XCircle, Ticket as TicketIcon } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

// iter190 — FA void-tickets panel. Purpose-built for the duplicate-issuance
// cleanup workflow (poll-vs-webhook race, RCA order 760e5de2 …). Takes a
// newline / comma-separated list of ticket ids and a >= 12-char reason;
// confirms before submitting; renders a per-ticket outcome table so the
// FA sees exactly which ids were voided, skipped, or not found.
export default function FAVoidTickets() {
  const { authAxios } = useAuth();
  const [idsText, setIdsText] = useState("");
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const parsedIds = Array.from(new Set(
    idsText
      .split(/[\s,]+/)
      .map(s => s.trim())
      .filter(Boolean)
  ));
  const canSubmit = parsedIds.length > 0 && reason.trim().length >= 12 && !submitting;

  const submit = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    setResult(null);
    try {
      const r = await authAxios.post("/finance-admin/tickets/void", {
        ticket_ids: parsedIds,
        reason: reason.trim(),
      });
      setResult(r.data);
      toast.success(`Voided ${r.data.modified_count} of ${r.data.requested} ticket(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Void failed");
    } finally {
      setSubmitting(false);
    }
  };

  const outcomeStyle = (o) => {
    if (o === "voided") return { color: "text-emerald-700", bg: "bg-emerald-50", icon: CheckCircle2 };
    if (o === "already_void") return { color: "text-slate-500", bg: "bg-slate-50", icon: CheckCircle2 };
    if (o === "not_found") return { color: "text-red-700", bg: "bg-red-50", icon: XCircle };
    return { color: "text-amber-700", bg: "bg-amber-50", icon: AlertTriangle }; // skipped_used etc.
  };

  return (
    <div className="space-y-4" data-testid="fa-void-tickets">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-semibold mb-1">Danger zone — voiding is one-way.</p>
          <p className="text-xs">Only tickets currently <span className="font-mono bg-white px-1 rounded">paid</span> will be flipped to <span className="font-mono bg-white px-1 rounded">void</span>. Tickets already scanned at the gate (<span className="font-mono bg-white px-1 rounded">used</span>) are skipped. Every call is audit-logged with your email + reason.</p>
        </div>
      </div>

      <div className="bg-white rounded-lg p-4 shadow-sm space-y-3">
        <div>
          <label className="block text-sm font-semibold text-[#1e3a5f] mb-1">Ticket IDs</label>
          <p className="text-xs text-slate-500 mb-2">Paste full ticket UUIDs — one per line, or comma / space separated.</p>
          <textarea
            value={idsText}
            onChange={(e) => setIdsText(e.target.value)}
            placeholder="5293502b-0bf2-426f-bcb5-f6a3532282e7&#10;8da1a61b-e0eb-43a5-86d2-4b333c4f684d&#10;..."
            rows={8}
            className="w-full font-mono text-xs border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30"
            data-testid="void-tickets-ids-input"
          />
          <p className="text-xs text-slate-500 mt-1" data-testid="void-tickets-parsed-count">
            {parsedIds.length} unique ID{parsedIds.length === 1 ? "" : "s"} detected
          </p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#1e3a5f] mb-1">Reason <span className="text-slate-400 font-normal">(≥ 12 chars, audit-logged)</span></label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Duplicate issuance from poll-vs-webhook race on order 760e5de2 — voided burst 1"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30 text-sm"
            data-testid="void-tickets-reason-input"
          />
          {reason.length > 0 && reason.trim().length < 12 && (
            <p className="text-xs text-red-600 mt-1">Reason must be at least 12 characters ({reason.trim().length}/12)</p>
          )}
        </div>

        <button
          onClick={() => setConfirmOpen(true)}
          disabled={!canSubmit}
          className="w-full py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="void-tickets-submit-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <TicketIcon className="w-4 h-4" />}
          Void {parsedIds.length} ticket{parsedIds.length === 1 ? "" : "s"}
        </button>
      </div>

      {result && (
        <div className="bg-white rounded-lg p-4 shadow-sm" data-testid="void-tickets-result">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="font-bold text-[#1e3a5f]">Result</h3>
            <span className="text-xs text-slate-500">Voided at {new Date(result.voided_at).toLocaleString()}</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center mb-4">
            <div className="bg-slate-50 rounded p-2"><p className="text-xs text-slate-500">Requested</p><p className="text-lg font-bold text-[#1e3a5f]" data-testid="void-result-requested">{result.requested}</p></div>
            <div className="bg-slate-50 rounded p-2"><p className="text-xs text-slate-500">Matched</p><p className="text-lg font-bold text-[#1e3a5f]" data-testid="void-result-matched">{result.matched_count}</p></div>
            <div className="bg-emerald-50 rounded p-2"><p className="text-xs text-emerald-700">Voided</p><p className="text-lg font-bold text-emerald-700" data-testid="void-result-voided">{result.modified_count}</p></div>
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Ticket ID</th>
                  <th className="text-left px-3 py-2 font-semibold">Order ID</th>
                  <th className="text-left px-3 py-2 font-semibold">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {result.per_ticket.map((row) => {
                  const s = outcomeStyle(row.outcome);
                  const Icon = s.icon;
                  return (
                    <tr key={row.id} className={`border-t border-slate-100 ${s.bg}`} data-testid={`void-row-${row.id}`}>
                      <td className="px-3 py-1.5 font-mono truncate max-w-[240px]">{row.id}</td>
                      <td className="px-3 py-1.5 font-mono text-slate-500 truncate max-w-[200px]">{row.order_id || "—"}</td>
                      <td className={`px-3 py-1.5 font-semibold ${s.color} flex items-center gap-1.5`}>
                        <Icon className="w-3.5 h-3.5" /> {row.outcome}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="void-tickets-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Void {parsedIds.length} ticket{parsedIds.length === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will flip <span className="font-semibold">paid</span> tickets to <span className="font-semibold">void</span> in this tenant. Tickets already scanned at the gate (<span className="font-mono text-xs">used</span>) will be skipped. Your email and the reason below will be recorded in the audit log.
              <br /><br />
              <span className="block bg-slate-50 border border-slate-200 rounded p-2 text-xs font-mono text-slate-700 max-h-24 overflow-y-auto">{reason.trim()}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="void-tickets-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={submit} className="bg-red-600 hover:bg-red-700" data-testid="void-tickets-confirm-btn">
              Yes, void {parsedIds.length}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
